import type { BusinessKind, KbReadinessKey } from '../../types/api';
import type { KnowledgeBaseSection } from '../knowledge-base/sections';

/**
 * Every label that follows the brand's kind, in one table.
 *
 * A personal brand's knowledge base is the same tables as a business's; what
 * changes is what the rows mean. `products` are a person's offers, `proof
 * points` their credentials, the logo a headshot or mark. Field names, API
 * calls and validation never change with the kind. Only what the screen calls
 * things does, and this is where it is decided so the onboarding form, the
 * settings form, the knowledge base tabs and the readiness card agree.
 */
export type BrandKind = BusinessKind;

export interface FieldCopy {
  label: string;
  placeholder: string;
  hint: string;
}

export interface BrandKindCopy {
  kind: BrandKind;
  /** "Business" / "Personal brand" — the tag beside a brand's name. */
  tag: string;
  /** "business" / "personal brand" — the noun in a sentence. */
  noun: string;
  /** The card on the kind step. */
  card: { title: string; body: string };
  name: FieldCopy;
  /** Personal brands only; null hides the field. */
  headline: FieldCopy | null;
  industry: FieldCopy;
  valueProposition: FieldCopy;
  targetAudience: FieldCopy;
  tone: FieldCopy;
  tagline: FieldCopy;
  neverSayHint: string;
  goals: readonly { value: string; label: string }[];
  customGoalPlaceholder: string;
  /** The labelled brief the paste step opens with. */
  template: string;
  /** Knowledge-base tab labels, by section key. */
  sections: Readonly<Record<KnowledgeBaseSection, string>>;
  /** Section ledes and the "add" verbs, where they name the record. */
  records: {
    products: { lede: string; empty: string; add: string; singular: string };
    proofPoints: { lede: string; empty: string; add: string; singular: string };
    voice: { lede: string; empty: string; add: string };
    identity: {
      lede: string;
      /** What you do: the business row's `value_proposition`. */
      about: FieldCopy;
      /** Who it's for: the business row's `target_audience`. */
      audience: FieldCopy;
      tone: string;
      tonePlaceholder: string;
      tagline: string;
      logo: string;
    };
  };
  /** Readiness card labels and nudges, by checklist key. */
  readiness: Readonly<Record<KbReadinessKey, { label: string; nudge: string }>>;
}

const BUSINESS_GOALS = [
  { value: 'Build brand awareness', label: 'Build brand awareness' },
  { value: 'Grow followers', label: 'Grow followers' },
  { value: 'Drive website traffic', label: 'Drive website traffic' },
  { value: 'Generate leads', label: 'Generate leads' },
  { value: 'Sell products', label: 'Sell products' },
  { value: 'Launch something new', label: 'Launch something new' },
  { value: 'Educate customers', label: 'Educate customers' },
  { value: 'Fill local footfall', label: 'Fill local footfall' },
] as const;

const PERSONAL_GOALS = [
  { value: 'Build authority', label: 'Build authority' },
  { value: 'Grow my audience', label: 'Grow my audience' },
  { value: 'Attract clients or opportunities', label: 'Attract clients or opportunities' },
  { value: 'Share what I am learning', label: 'Share what I am learning' },
  { value: 'Get hired or booked', label: 'Get hired or booked' },
  { value: 'Launch something new', label: 'Launch something new' },
  { value: 'Document the journey', label: 'Document the journey' },
] as const;

/**
 * The paste templates. Labels are the ones the server's parser recognises
 * first; it accepts both sets whatever the kind, so a brief written for one
 * card still reads on the other.
 */
const BUSINESS_TEMPLATE = [
  'Business name:',
  'Industry:',
  'What you do:',
  'Who you sell to:',
  'Tone:',
  'Tagline:',
  'Never say:',
  'Platforms:',
  'Goals:',
  '',
].join('\n');

const PERSONAL_TEMPLATE = [
  'Your name:',
  'Headline:',
  'Field:',
  'Known for:',
  'Who you speak to:',
  'Voice:',
  'Bio line:',
  'Never say:',
  'Platforms:',
  'Goals:',
  '',
].join('\n');

const BUSINESS_COPY: BrandKindCopy = {
  kind: 'business',
  tag: 'Business',
  noun: 'business',
  card: {
    title: 'Business',
    body: 'A company, shop or service. The brand profile holds what it sells, its proof and its brand voice, and the copy speaks as the business.',
  },
  name: {
    label: 'Business name',
    placeholder: 'Northside Coffee Roasters',
    hint: 'The name that goes on the work.',
  },
  headline: null,
  industry: {
    label: 'Industry',
    placeholder: 'Specialty coffee retail',
    hint: 'Picks the trade news the feed follows.',
  },
  valueProposition: {
    label: 'What you do',
    placeholder: 'Small-batch roasting with beans traced to a single farm.',
    hint: 'Plain language, what you do better than anyone nearby.',
  },
  targetAudience: {
    label: 'Who you sell to',
    placeholder: 'Office workers within walking distance who care where their coffee comes from.',
    hint: 'Who buys, and what they care about.',
  },
  tone: {
    label: 'Tone',
    placeholder: 'Warm, direct, a little nerdy',
    hint: 'How the captions should sound.',
  },
  tagline: {
    label: 'Tagline',
    placeholder: 'Roasted this week, not this quarter.',
    hint: 'Anchors headline copy and slide one.',
  },
  neverSayHint: 'Words and phrases the caption check will refuse. Enter or comma adds one.',
  goals: BUSINESS_GOALS,
  customGoalPlaceholder: 'Book more weekend catering',
  template: BUSINESS_TEMPLATE,
  sections: {
    'brand-identity': 'About your brand',
    looks: 'Visual style',
    voice: 'Example posts',
    products: 'Products',
    assets: 'Images & logos',
    'proof-points': 'Testimonials & results',
    inspiration: 'Inspiration',
    'brand-memory': 'Brand memory',
    integrations: 'Connected accounts',
  },
  records: {
    products: {
      lede: "What you sell, in the words you'd like your posts to use.",
      empty: 'No products yet. Add the first thing you sell.',
      add: 'Add product',
      singular: 'product',
    },
    proofPoints: {
      lede: 'Facts we can safely use in your posts — numbers, awards, reviews.',
      empty: 'No testimonials or results yet. Add a claim and what backs it.',
      add: 'Add testimonial',
      singular: 'testimonial or result',
    },
    voice: {
      lede: 'Real posts in your voice. We read them before every draft.',
      empty: 'No examples yet. Paste a caption that already sounds like you.',
      add: 'Add post',
    },
    identity: {
      lede: 'What you do and who it is for, then tone, tagline, colors, fonts, logo and the words to avoid.',
      about: {
        label: 'What you do',
        hint: "One or two plain sentences, as you'd tell a neighbour.",
        placeholder: 'e.g. A small roastery and café on the harbour.',
      },
      audience: {
        label: "Who it's for",
        hint: 'The people you want reading your posts.',
        placeholder: 'e.g. Locals on their way to work, and home brewers.',
      },
      tone: 'Tone of voice',
      tonePlaceholder: 'e.g. Warm, plainspoken, a little wry',
      tagline: 'Tagline',
      logo: 'Logo',
    },
  },
  readiness: {
    what_you_do: {
      label: 'What you do',
      nudge: 'Start with what you do — every draft is written from it.',
    },
    who_its_for: {
      label: "Who it's for",
      nudge: "Say who it's for — drafts are written to the people who read them.",
    },
    tone: {
      label: 'Tone of voice',
      nudge: 'Set your tone of voice — without it every caption comes back sounding like anyone.',
    },
    tagline: {
      label: 'Tagline',
      nudge: 'Add your tagline — it anchors headline copy and slide one.',
    },
    logo: {
      label: 'Logo',
      nudge: 'Upload your logo — anything we make goes out unbranded until it is added.',
    },
    never_say: {
      label: 'Words to avoid',
      nudge: 'Add words to avoid — the caption check has nothing to look for yet.',
    },
    voice_examples: {
      label: 'Example posts',
      nudge: 'Without example posts, drafts sound generic.',
    },
    products: {
      label: 'Products',
      nudge: 'Add products — captions stay vague about what you actually sell.',
    },
    proof_points: {
      label: 'Testimonials & results',
      nudge: "No testimonials or results yet — your posts can't make claims we can't back up.",
    },
    assets: {
      label: 'Images & logos',
      nudge: 'Upload images and logos — we have no pictures of yours to work with.',
    },
    inspiration: {
      label: 'Inspiration',
      nudge: 'Add a few pictures you like — we learn your taste from them.',
    },
  },
};

const PERSONAL_COPY: BrandKindCopy = {
  kind: 'personal',
  tag: 'Personal brand',
  noun: 'personal brand',
  card: {
    title: 'Personal brand',
    body: 'You are the brand. The brand profile holds what you are known for, your testimonials and results and your services, and the copy is written as you, in the first person.',
  },
  name: {
    label: 'Your name',
    placeholder: 'Amman Khan',
    hint: 'As it appears on your profiles.',
  },
  headline: {
    label: 'Headline',
    placeholder: 'Founder, Kaizen Global',
    hint: 'The one line under your name: role, company, or what you do.',
  },
  industry: {
    label: 'Field',
    placeholder: 'SaaS for small teams',
    hint: 'Your niche. Picks the news the feed follows.',
  },
  valueProposition: {
    label: 'What you are known for',
    placeholder: 'Building small tools and writing plainly about how they get built.',
    hint: 'The topics and the angle people come to you for.',
  },
  targetAudience: {
    label: 'Who you speak to',
    placeholder: 'Founders and operators who ship their own product.',
    hint: 'Who follows you, and what they want from you.',
  },
  tone: {
    label: 'Your voice',
    placeholder: 'Direct, first person, no hype',
    hint: 'How your posts should sound.',
  },
  tagline: {
    label: 'Bio line',
    placeholder: 'I build small software and write about it.',
    hint: 'One line that could sit in your bio.',
  },
  neverSayHint: 'Words you never use. Enter or comma adds one.',
  goals: PERSONAL_GOALS,
  customGoalPlaceholder: 'Land two speaking slots this year',
  template: PERSONAL_TEMPLATE,
  sections: {
    'brand-identity': 'About your brand',
    looks: 'Visual style',
    voice: 'Posts like yours',
    products: 'Services',
    assets: 'Photos',
    'proof-points': 'Testimonials & results',
    inspiration: 'Inspiration',
    'brand-memory': 'Brand memory',
    integrations: 'Connected accounts',
  },
  records: {
    products: {
      lede: 'What you offer: services, courses, products, a newsletter. What a post may point to.',
      empty: 'No services yet. Add the first thing people can buy or sign up for.',
      add: 'Add service',
      singular: 'service',
    },
    proofPoints: {
      lede: 'Facts we can safely use in your posts — numbers, awards, reviews.',
      empty: 'No testimonials or results yet. Add a result, a review or a number you can back.',
      add: 'Add testimonial',
      singular: 'testimonial or result',
    },
    voice: {
      lede: 'Posts that already sound like you. We read them before every draft.',
      empty: 'No examples yet. Paste a post you wrote and liked.',
      add: 'Add post',
    },
    identity: {
      lede: 'What you are known for and who you speak to, then your voice, bio line, colors, fonts, headshot and the words to avoid.',
      about: {
        label: 'What you are known for',
        hint: 'One or two plain sentences: the topics and the angle people come to you for.',
        placeholder: 'e.g. Building small tools and writing plainly about how they get built.',
      },
      audience: {
        label: 'Who you speak to',
        hint: 'The people you want reading your posts.',
        placeholder: 'e.g. Founders and operators who ship their own product.',
      },
      tone: 'Your voice',
      tonePlaceholder: 'e.g. Direct, first person, no hype',
      tagline: 'Bio line',
      logo: 'Headshot or mark',
    },
  },
  readiness: {
    what_you_do: {
      label: 'What you are known for',
      nudge: 'Start with what you are known for — every draft is written from it.',
    },
    who_its_for: {
      label: 'Who you speak to',
      nudge: 'Say who you speak to — drafts are written to the people who read them.',
    },
    tone: {
      label: 'Your voice',
      nudge: 'Describe your voice — without it every post comes back sounding like anyone.',
    },
    tagline: {
      label: 'Bio line',
      nudge: 'Add your bio line — it anchors headline copy and slide one.',
    },
    logo: {
      label: 'Headshot or mark',
      nudge: 'Upload a headshot or mark — anything we make goes out unbranded until it is added.',
    },
    never_say: {
      label: 'Words to avoid',
      nudge: 'Add words to avoid — the caption check has nothing to look for yet.',
    },
    voice_examples: {
      label: 'Posts like yours',
      nudge: 'Without posts you wrote, drafts sound generic.',
    },
    products: {
      label: 'Services',
      nudge: 'Add your services — posts stay vague about what people can actually get from you.',
    },
    proof_points: {
      label: 'Testimonials & results',
      nudge: "No testimonials or results yet — your posts can't make claims we can't back up.",
    },
    assets: {
      label: 'Photo library',
      nudge: 'Upload photos — we have no pictures of yours to work with.',
    },
    inspiration: {
      label: 'Inspiration',
      nudge: 'Add a few pictures you like — we learn your taste from them.',
    },
  },
};

export const BRAND_KINDS: readonly BrandKind[] = ['business', 'personal'];

export function isBrandKind(value: unknown): value is BrandKind {
  return value === 'business' || value === 'personal';
}

/** The copy table for a kind. An absent or unknown kind reads as a business. */
export function brandKindCopy(kind: BrandKind | null | undefined): BrandKindCopy {
  return kind === 'personal' ? PERSONAL_COPY : BUSINESS_COPY;
}

/** Every preset goal across both kinds, for canonicalising a parsed goal. */
export const ALL_PRESET_GOALS: readonly string[] = [
  ...BUSINESS_GOALS.map((option) => option.value),
  ...PERSONAL_GOALS.map((option) => option.value),
];
